// MST
import { types, flow, cast, detach } from 'mobx-state-tree'

// Interfaces
import {
  ICertificationWebpageSelfAssessmentApiDetailResponse,
  ICertificationWebpageSelfAssessmentApiDownloadDocumentResponse,
  ICertificationWebpageSelfAssessmentApiListResponse,
  ICertificationWebpageSelfAssessmentApiNullResponse,
  ICertificationWebpageSelfAssessmentApiUploadDocumentResponse,
} from 'interfaces/webpage/certification/registration/self-assessment/self-assessment-api.interface'
import {
  ICertificationWebpageSelfAssessmentGetDetailAttrs,
  ICertificationWebpageSelfAssessmentGetListAttrs,
  ICertificationWebpageSelfAssessmentUpdateUserAnswerAttrs,
  ICertificationWebpageSelfAssessmentUploadDocumentAttrs,
  ICertificationWebpageSelfAssessmentDeleteDocumentAttrs,
  ICertificationWebpageSelfAssessmentDownloadDocumentAttrs,
} from 'interfaces/webpage/certification/registration/self-assessment/self-assessment-view.interface'
import {
  ISharedCommonFileAnswerAttrs,
  ISharedCommonLoadingAttrs,
  ISharedCommonLoadingQuestionSetAttrs,
  ISharedCommonUpdateAnswerAttrs,
} from 'interfaces/shared/shared-common.interface'

// Extensions
import { withEnvironment } from 'models/extensions/with-environment'
import {
  withHelper_competencyUnitQuestionSetModel,
  withHelper_commonLoadingModel,
  withHelper_competencyUnitModel,
  withHelper_commonMapLoading,
  withHelper_commonMapQuestionSet,
  withHelper_commonMapQuestionSetLoading,
  withHelper_commonMapAnswerValue,
  withHelper_commonMapAnswerFiles,
  withHelper_commonGetPreviousAnswerValue,
} from 'models/extensions/helper'

// Constant
import { LOADING, LOADING_KEY_TYPE } from 'constant/shared.constant'

// Lodash
import cloneDeep from 'lodash/cloneDeep'
import { withHelper_fileDownload } from 'models/extensions/with-helper'

// Loading
const { BASE, DETAIL, ACTION } = LOADING
const { ANSWER, ANSWER_FILES } = LOADING_KEY_TYPE

// Types
const { optional, array } = types

const CertificationWebpageSelfAssessmentModel = types
  .model('CertificationWebpageSelfAssessment')
  .props({
    certificationWebpageSelfAssessment_loading: withHelper_commonLoadingModel,
    certificationWebpageSelfAssessment_list: optional(array(withHelper_competencyUnitModel), []),
    certificationWebpageSelfAssessment_detail: optional(withHelper_competencyUnitQuestionSetModel, {}),
  })
  .actions(self => ({
    certificationWebpageSelfAssessment_SET_LOADING: (payload: ISharedCommonLoadingAttrs): void => {
      const map = withHelper_commonMapLoading(self.certificationWebpageSelfAssessment_loading, payload)

      detach(self.certificationWebpageSelfAssessment_loading)
      self.certificationWebpageSelfAssessment_loading = cast(map)
    },
    certificationWebpageSelfAssessment_SET_LIST: (
      payload: ICertificationWebpageSelfAssessmentApiListResponse['res']['data'],
    ): void => {
      self.certificationWebpageSelfAssessment_list = cast(payload)
    },
    certificationWebpageSelfAssessment_SET_DETAIL: (
      payload: ICertificationWebpageSelfAssessmentApiDetailResponse['res']['data'],
    ): void => {
      const map = cloneDeep({
        ...payload,
        questionSet: withHelper_commonMapQuestionSet(payload.questionSet),
      })

      detach(self.certificationWebpageSelfAssessment_detail)
      self.certificationWebpageSelfAssessment_detail = cast(map)
    },
    certificationWebpageSelfAssessment_MAP_LOADING_DETAIL: (payload: ISharedCommonLoadingQuestionSetAttrs): void => {
      const questionSet = withHelper_commonMapQuestionSetLoading(
        self.certificationWebpageSelfAssessment_detail.questionSet,
        payload,
      )
      const map = cloneDeep({
        ...self.certificationWebpageSelfAssessment_detail,
        questionSet,
      })

      detach(self.certificationWebpageSelfAssessment_detail)
      self.certificationWebpageSelfAssessment_detail = cast(map)
    },
    certificationWebpageSelfAssessment_MAP_ANSWER_DETAIL: (payload: ISharedCommonUpdateAnswerAttrs): void => {
      const questionSet = withHelper_commonMapAnswerValue(self.certificationWebpageSelfAssessment_detail.questionSet, payload)
      const map = cloneDeep({
        ...self.certificationWebpageSelfAssessment_detail,
        questionSet,
      })

      detach(self.certificationWebpageSelfAssessment_detail)
      self.certificationWebpageSelfAssessment_detail = cast(map)
    },
    certificationWebpageSelfAssessment_MAP_ANSWER_FILE_DETAIL: (payload: ISharedCommonFileAnswerAttrs): void => {
      const questionSet = withHelper_commonMapAnswerFiles(self.certificationWebpageSelfAssessment_detail.questionSet, payload)
      const map = cloneDeep({
        ...self.certificationWebpageSelfAssessment_detail,
        questionSet,
      })

      detach(self.certificationWebpageSelfAssessment_detail)
      self.certificationWebpageSelfAssessment_detail = cast(map)
    },
    certificationWebpageSelfAssessment_CLEAR_LIST: (): void => {
      self.certificationWebpageSelfAssessment_list = cast([])
    },
    certificationWebpageSelfAssessment_CLEAR_DETAIL: (): void => {
      self.certificationWebpageSelfAssessment_detail = cast({})
    },
  }))
  .extend(withEnvironment)
  .actions(self => ({
    /**
     * @description Fetch self assessment
     *
     * @param {ICertificationWebpageSelfAssessmentGetListAttrs} payload
     *
     * @return {any} any
     */
    certificationWebpageSelfAssessment_fetchList: flow(function* (payload: ICertificationWebpageSelfAssessmentGetListAttrs) {
      self.certificationWebpageSelfAssessment_SET_LOADING({ type: BASE, value: true })

      const response: ICertificationWebpageSelfAssessmentApiListResponse =
        yield self.environment.certificationWebpageSelfAssessmentApi.certificationWebpageSelfAssessment_getList({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
        })

      // Do when response success
      if (response.ok) {
        self.certificationWebpageSelfAssessment_SET_LIST(response.res.data)

        if (payload?.onSuccess) payload.onSuccess()
      }

      // Do when response failed
      if (!response.ok) {
        if (payload?.onError) payload.onError()
      }

      self.certificationWebpageSelfAssessment_SET_LOADING({ type: BASE, value: false })
    }),

    /**
     * @description Fetch self assessment detail
     *
     * @param {ICertificationWebpageSelfAssessmentGetDetailAttrs} payload
     *
     * @return {any} any
     */
    certificationWebpageSelfAssessment_fetchDetail: flow(function* (payload: ICertificationWebpageSelfAssessmentGetDetailAttrs) {
      self.certificationWebpageSelfAssessment_SET_LOADING({ type: DETAIL, value: true })

      const response: ICertificationWebpageSelfAssessmentApiDetailResponse =
        yield self.environment.certificationWebpageSelfAssessmentApi.certificationWebpageSelfAssessment_getDetail({
          params: { ...payload.params },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
        })

      // Do when response success
      if (response.ok) {
        self.certificationWebpageSelfAssessment_SET_DETAIL(response.res.data)

        if (payload?.onSuccess) payload.onSuccess()
      }

      // Do when response failed
      if (!response.ok) {
        if (payload?.onError) payload.onError()
      }

      self.certificationWebpageSelfAssessment_SET_LOADING({ type: DETAIL, value: false })
    }),

    /**
     * @description Update self assessment user answer
     *
     * @param {ICertificationWebpageSelfAssessmentUpdateUserAnswerAttrs} payload
     *
     * @return {any} any
     */
    certificationWebpageSelfAssessment_update: flow(function* (
      payload: ICertificationWebpageSelfAssessmentUpdateUserAnswerAttrs,
    ) {
      const { assessmentId, questionSetId, resultId, resultDetailId, currentValue, value } = payload.params

      // Start Loading
      self.certificationWebpageSelfAssessment_MAP_LOADING_DETAIL({
        questionSetId,
        resultId,
        resultDetailId,
        type: ANSWER,
        loadingType: ACTION,
        value: true,
      })

      // Map value to current state
      self.certificationWebpageSelfAssessment_MAP_ANSWER_DETAIL({ questionSetId, resultId, resultDetailId, currentValue, value })

      const response: ICertificationWebpageSelfAssessmentApiNullResponse =
        yield self.environment.certificationWebpageSelfAssessmentApi.certificationWebpageSelfAssessment_updateUserAnswer({
          params: { assessmentId },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
        })

      // Do when response success
      if (response.ok) {
        if (payload?.onSuccess) payload.onSuccess()
      }

      // Do when response failed
      if (!response.ok) {
        // Revert value to previous value
        self.certificationWebpageSelfAssessment_MAP_ANSWER_DETAIL({
          questionSetId,
          resultId,
          resultDetailId,
          currentValue,
          value: withHelper_commonGetPreviousAnswerValue(currentValue),
        })

        if (payload?.onError) payload.onError()
      }

      // Stop Loading
      self.certificationWebpageSelfAssessment_MAP_LOADING_DETAIL({
        questionSetId,
        resultId,
        resultDetailId,
        type: ANSWER,
        loadingType: ACTION,
        value: false,
      })
    }),

    /**
     * @description Upload self assessment competency
     *
     * @param {ICertificationWebpageSelfAssessmentUploadDocumentAttrs} payload
     *
     * @return {any} any
     */
    certificationWebpageSelfAssessment_uploadDocument: flow(function* (
      payload: ICertificationWebpageSelfAssessmentUploadDocumentAttrs,
    ) {
      const { assessmentId, questionSetId, resultId, resultDetailId, fileId, value } = payload.params

      // Start Loading
      self.certificationWebpageSelfAssessment_MAP_LOADING_DETAIL({
        questionSetId,
        resultId,
        resultDetailId,
        fileId,
        type: ANSWER_FILES,
        loadingType: ACTION,
        value: true,
      })

      const response: ICertificationWebpageSelfAssessmentApiUploadDocumentResponse =
        yield self.environment.certificationWebpageSelfAssessmentApi.certificationWebpageSelfAssessment_uploadDocument({
          params: { assessmentId },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
        })

      // Do when response success
      if (response.ok) {
        // Map data when success
        self.certificationWebpageSelfAssessment_MAP_ANSWER_FILE_DETAIL({
          questionSetId,
          resultId,
          resultDetailId,
          fileId: fileId!,
          value: { ...response.res.data, userOriginalFileName: value.name },
          type: 'edit',
        })

        if (payload?.onSuccess) payload.onSuccess()
      }

      // Do when response failed
      if (!response.ok) {
        if (payload?.onError) payload.onError()
      }

      // Stop Loading
      self.certificationWebpageSelfAssessment_MAP_LOADING_DETAIL({
        questionSetId,
        resultId,
        resultDetailId,
        fileId,
        type: ANSWER_FILES,
        loadingType: ACTION,
        value: false,
      })
    }),

    /**
     * @description Delete self assessment competency document
     *
     * @param {ICertificationWebpageSelfAssessmentDeleteDocumentAttrs} payload
     *
     * @return {any} any
     */
    certificationWebpageSelfAssessment_deleteDocument: flow(function* (
      payload: ICertificationWebpageSelfAssessmentDeleteDocumentAttrs,
    ) {
      const { userFileTransactionId, questionSetId, resultId, resultDetailId, fileId } = payload.params

      // Start Loading
      self.certificationWebpageSelfAssessment_MAP_LOADING_DETAIL({
        questionSetId,
        resultId,
        resultDetailId,
        fileId,
        type: ANSWER_FILES,
        loadingType: ACTION,
        value: true,
      })

      const response: ICertificationWebpageSelfAssessmentApiNullResponse =
        yield self.environment.certificationWebpageSelfAssessmentApi.certificationWebpageSelfAssessment_deleteDocument({
          params: { userFileTransactionId },
          requestConfig: { ...payload.requestConfig },
        })

      // Do when response success
      if (response.ok) {
        // Map data when success
        self.certificationWebpageSelfAssessment_MAP_ANSWER_FILE_DETAIL({
          questionSetId,
          resultId,
          resultDetailId,
          fileId: fileId!,
          type: 'delete',
        })

        if (payload?.onSuccess) payload.onSuccess()
      }

      // Do when response failed
      if (!response.ok) {
        if (payload?.onError) payload.onError()
      }

      // Stop Loading
      self.certificationWebpageSelfAssessment_MAP_LOADING_DETAIL({
        questionSetId,
        resultId,
        resultDetailId,
        fileId,
        type: ANSWER_FILES,
        loadingType: ACTION,
        value: false,
      })
    }),

    /**
     * @description Download document of self assessment
     *
     * @param {ICertificationWebpageSelfAssessmentDownloadDocumentAttrs} payload
     *
     * @return {any} any
     */
    certificationWebpageSelfAssessment_downloadDocument: flow(function* (
      payload: ICertificationWebpageSelfAssessmentDownloadDocumentAttrs,
    ) {
      const { blobId, questionSetId, resultId, resultDetailId, fileId } = payload.params

      // Start Loading
      self.certificationWebpageSelfAssessment_MAP_LOADING_DETAIL({
        questionSetId,
        resultId,
        resultDetailId,
        fileId,
        type: ANSWER_FILES,
        loadingType: ACTION,
        value: true,
      })

      const response: ICertificationWebpageSelfAssessmentApiDownloadDocumentResponse =
        yield self.environment.certificationWebpageSelfAssessmentApi.certificationWebpageSelfAssessment_downloadDocument({
          params: { blobId },
          requestConfig: { ...payload.requestConfig },
        })

      // Do when response success
      if (response.ok) {
        withHelper_fileDownload(response.res, response.headers!)

        if (payload?.onSuccess) payload.onSuccess()
      }

      // Do when response failed
      if (!response.ok) {
        if (payload?.onError) payload.onError()
      }

      // Stop Loading
      self.certificationWebpageSelfAssessment_MAP_LOADING_DETAIL({
        questionSetId,
        resultId,
        resultDetailId,
        fileId,
        type: ANSWER_FILES,
        loadingType: ACTION,
        value: false,
      })
    }),

    /**
     * @description Preview document of self assessment
     *
     * @param {ICertificationWebpageSelfAssessmentDownloadDocumentAttrs} payload
     *
     * @return {any} any
     */
    certificationWebpageSelfAssessment_previewDocument: flow(function* (
      payload: ICertificationWebpageSelfAssessmentDownloadDocumentAttrs,
    ) {
      const { blobId, questionSetId, resultId, resultDetailId, fileId } = payload.params

      // Start Loading
      self.certificationWebpageSelfAssessment_MAP_LOADING_DETAIL({
        questionSetId,
        resultId,
        resultDetailId,
        fileId,
        type: ANSWER_FILES,
        loadingType: ACTION,
        value: true,
      })

      const response: ICertificationWebpageSelfAssessmentApiDownloadDocumentResponse =
        yield self.environment.certificationWebpageSelfAssessmentApi.certificationWebpageSelfAssessment_downloadDocument({
          params: { blobId },
          requestConfig: { ...payload.requestConfig },
        })

      // Do when response success
      if (response.ok) {
        // if (payload?.onSuccess) payload.onSuccess({res: response.res, headers: response.headers!})
        if (payload?.onSuccess) payload.onSuccess({data: response.res, headers: response.headers!})
      }

      // Do when response failed
      if (!response.ok) {
        if (payload?.onError) payload.onError()
      }

      // Stop Loading
      self.certificationWebpageSelfAssessment_MAP_LOADING_DETAIL({
        questionSetId,
        resultId,
        resultDetailId,
        fileId,
        type: ANSWER_FILES,
        loadingType: ACTION,
        value: false,
      })
    }),
  }))

export const createCertificationWebpageSelfAssessmentDefaultModel = () => optional(CertificationWebpageSelfAssessmentModel, {})
